import type { CameraFit } from '../viewer/adapter.js';

const ROUND_DECIMALS = 4;
const ROUND_MULT = 10 ** ROUND_DECIMALS;

// Bounds per validazione decode (Codex iter 1 MINOR fix): respinge camera
// patologiche che renderebbero il viewer inutilizzabile e ricade sull'auto-fit.
const FOV_MIN = 10;
const FOV_MAX = 170;
const COORD_LIMIT = 10_000;

function round(n: number): number {
  return Math.round(n * ROUND_MULT) / ROUND_MULT;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlDecode(encoded: string): Uint8Array | null {
  try {
    const padded = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padLen = (4 - (padded.length % 4)) % 4;
    const bin = atob(padded + '='.repeat(padLen));
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

export function encodeCameraState(cam: CameraFit): string {
  const arr = [
    round(cam.pos[0]),
    round(cam.pos[1]),
    round(cam.pos[2]),
    round(cam.lookAt[0]),
    round(cam.lookAt[1]),
    round(cam.lookAt[2]),
    round(cam.fov),
  ];
  const json = JSON.stringify(arr);
  const bytes = new TextEncoder().encode(json);
  return base64UrlEncode(bytes);
}

function isInRange(value: number, min: number, max: number): boolean {
  return Number.isFinite(value) && value >= min && value <= max;
}

export function decodeCameraState(encoded: string): CameraFit | null {
  const bytes = base64UrlDecode(encoded);
  if (bytes === null) return null;
  let json: string;
  try {
    json = new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed) || parsed.length !== 7) return null;
  const nums = parsed as readonly unknown[];
  for (const v of nums) {
    if (typeof v !== 'number') return null;
  }
  const [px, py, pz, lx, ly, lz, fov] = nums as readonly number[];
  // Coordinate bounds (defense against patologic shared URLs)
  if (
    !isInRange(px as number, -COORD_LIMIT, COORD_LIMIT) ||
    !isInRange(py as number, -COORD_LIMIT, COORD_LIMIT) ||
    !isInRange(pz as number, -COORD_LIMIT, COORD_LIMIT) ||
    !isInRange(lx as number, -COORD_LIMIT, COORD_LIMIT) ||
    !isInRange(ly as number, -COORD_LIMIT, COORD_LIMIT) ||
    !isInRange(lz as number, -COORD_LIMIT, COORD_LIMIT)
  ) {
    return null;
  }
  if (!isInRange(fov as number, FOV_MIN, FOV_MAX)) return null;
  return {
    pos: [px as number, py as number, pz as number],
    lookAt: [lx as number, ly as number, lz as number],
    fov: fov as number,
  };
}
